import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';

import Header from 'components/Common/Header';
import TextInput from 'components/Common/TextInput';
import AtlantaParishes from 'components/Parishes&Organizations/AtlantaParishes';
import { Card } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import MuiLoader from 'components/Common/Loader';
import { fetchParishData } from 'redux/slices/parishesAndOrganisations/parishOrganizationSlice';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import { updateTableState } from 'redux/slices/common/appSlice';
import { AppDispatch } from '../../redux/store';
import { defaultPageSize } from 'appConfig';
import { debounce } from 'utils/helpers';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});
const Parishes: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { data: parishData, loading, total } = useSelector((state: RootState) => state.parish.parishesList);
    const { pageNumber = 0, pageSize = defaultPageSize } = useSelector((state: RootState) => state.app.table) || {};
    const selectedParishId = useSelector(selectSelectedParishId);
    const [searchText, setSearchText] = useState<string>('');
    const prevSelectedParishIdRef = useRef(selectedParishId);

    // On an actual parish switch, reset the shared table page back to 0 — the empty-search
    // branch of fetchParishData scopes results to selectedParishId (parish children), and a
    // stale later page left over from the previous parish can otherwise request an
    // out-of-range page against the new parish's (possibly smaller) result set and come back
    // empty even though it has records. Also clear any typed search (mirrors Community.tsx's
    // H2 pattern). Deliberately keyed only on selectedParishId, not pageNumber/pageSize, so
    // this doesn't also fire on ordinary page-to-page navigation.
    useEffect(() => {
        setSearchText('');
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches
    }, [selectedParishId, dispatch]);

    useEffect(() => {
        // pageNumber/pageSize are read fresh from state.app.table inside the thunk (see
        // parishOrganizationSlice.ts) rather than passed as arguments here — a value read
        // from this render's closure can be stale on the very first render after the reset
        // above (its dispatch lands one render later) or on a fresh mount carrying over a
        // leftover page number from a different screen, and fetching against a stale value
        // would show the wrong page's data under a pagination control that already reads
        // the corrected one.
        const parishChanged = prevSelectedParishIdRef.current !== selectedParishId;
        prevSelectedParishIdRef.current = selectedParishId;
        if (parishChanged && (pageNumber !== 0 || searchText !== '')) {
            // The reset effect above is about to dispatch a pageNumber and/or searchText
            // change for the new parish — that settling re-triggers this effect with the
            // corrected values, so skip this transitional fetch rather than requesting a page
            // (or a search) that's about to be superseded. Only safe to skip because at least
            // one of those two will actually change (checked above) — otherwise nothing else
            // would ever re-trigger this effect for the new parish.
            return;
        }
        dispatch(fetchParishData({ searchText: parishChanged ? '' : searchText }));
    }, [dispatch, pageNumber, pageSize, searchText, selectedParishId]);

    const handleChangeSearch = useMemo(
        () =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches TextInput's untyped onChange
            debounce((e: any) => {
                const { value } = e.target || {};
                setSearchText(value || '');
                // New search results start over at page 1 — otherwise a search made while
                // sitting on page 3 requests an out-of-range page for the new result set.
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }, 700),
        [dispatch, pageSize]
    );

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader loading={loading} absolute />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <Card sx={{ mb: 1 }}>
                        <div style={{ padding: 16 }}>
                            {/* TextInput is an uncontrolled MUI field (no value prop), so
                                clearing searchText in state doesn't touch the visible DOM
                                value — remount on parish switch to actually clear the box. */}
                            <TextInput key={selectedParishId || ''} onChange={handleChangeSearch} />
                        </div>
                    </Card>
                    <AtlantaParishes data={parishData} loading={loading} totalCount={total} />
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Parishes;
